import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, ChevronRight, Gauge, Languages, Medal, ShieldCheck } from "lucide-react";
import ApplicantShell from "../components/ApplicantShell";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import B1SpeakingAudioRecorder from "../components/language/B1SpeakingAudioRecorder";
import config from "../config/config";
import { clearStoredAuth } from "../utils/authSession";

function getAuthHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function fetchJson(path, options = {}) {
  const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;
  const response = await fetch(`${config.API_BASE_URL}${path}`, {
    ...options,
    headers: {
      ...(!isFormData && options.body ? { "Content-Type": "application/json" } : {}),
      ...getAuthHeaders(),
      ...(options.headers || {}),
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data?.message || data?.detail || `Request failed (${response.status})`);
    error.status = response.status;
    error.detail = data?.detail || "";
    throw error;
  }
  return data;
}

function extractModules(attempt) {
  return attempt?.answers?.modules || {};
}

function buildDraftState(attempt) {
  const saved = attempt?.answers?.draft_responses || {};
  return {
    reading: saved.reading || {},
    listening: saved.listening || {},
    language_elements: saved.language_elements || {},
    writing_answer: saved.writing_answer || "",
    speaking_audio_meta: saved.speaking_audio_meta || null,
    speaking_status: attempt?.speaking_status || saved.speaking_status || "not_started",
    speaking_invalid_reason: attempt?.speaking_invalid_reason || saved.speaking_invalid_reason || "",
    modules: extractModules(attempt),
  };
}

function buildAttemptPayload(draftState) {
  return {
    reading_answers: Object.entries(draftState.reading || {}).map(([item_id, selected_option]) => ({ item_id, selected_option })),
    listening_answers: Object.entries(draftState.listening || {}).map(([item_id, selected_option]) => ({ item_id, selected_option })),
    language_elements_answers: Object.entries(draftState.language_elements || {}).map(([item_id, selected_option]) => ({ item_id, selected_option })),
    writing_answer: draftState.writing_answer || "",
  };
}

function formatDate(value) {
  if (!value) return "Noch offen";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });
}

function formatRelative(value) {
  if (!value) return "gerade";
  const diffMinutes = Math.max(1, Math.round((Date.now() - new Date(value).getTime()) / 60000));
  if (diffMinutes < 60) return `vor ${diffMinutes} Min.`;
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `vor ${diffHours} Std.`;
  return `vor ${Math.round(diffHours / 24)} Tg.`;
}

function formatScore(value) {
  return value === null || value === undefined ? "Noch offen" : `${Math.round(Number(value))}`;
}

function formatDelta(value) {
  if (value === null || value === undefined) return "Kein Vergleich";
  const number = Number(value);
  return `${number > 0 ? "+" : ""}${number.toFixed(1)}`;
}

function formatAttemptType(value) {
  if (value === "baseline") return "Baseline";
  if (value === "follow_up") return "Folgetest";
  return "Noch offen";
}

function formatReviewStatus(value) {
  if (value === "reviewed") return "Geprueft";
  if (value === "pending") return "In Pruefung";
  if (value === "not_required") return "Keine manuelle Pruefung noetig";
  return value || "Noch offen";
}

function formatPercentile(value) {
  return value ? `${value}. Perzentil` : "Noch offen";
}

function formatOutOfHundred(value) {
  return value ? `${value} von 100` : "Noch offen";
}

function toPercent(value) {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) return 0;
  return Math.max(0, Math.min(100, numeric));
}

function normalizePanel(value) {
  const allowed = new Set(["overview", "history", "visibility"]);
  return allowed.has(value) ? value : "overview";
}

function renderChoiceModule(items, fieldName, values, onChange) {
  return (items || []).map((item, index) => (
    <div key={item.id} className="dashboard-question-card">
      <p className="dashboard-question-card__title">{index + 1}. {item.prompt}</p>
      {item.transcript ? <p className="dashboard-question-card__copy">{item.transcript}</p> : null}
      <div className="dashboard-question-options">
        {item.options?.map((option, optionIndex) => (
          <label key={`${item.id}-${optionIndex}`} className="dashboard-option-row">
            <input
              type="radio"
              name={`${fieldName}-${item.id}`}
              checked={values?.[item.id] === optionIndex}
              onChange={() => onChange(fieldName, item.id, optionIndex)}
            />
            <span>{option}</span>
          </label>
        ))}
      </div>
    </div>
  ));
}

export default function ApplicantDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [snapshot, setSnapshot] = useState(null);
  const [snapshotLoading, setSnapshotLoading] = useState(true);
  const [snapshotError, setSnapshotError] = useState("");
  const [attempts, setAttempts] = useState([]);
  const [latestAttempt, setLatestAttempt] = useState(null);
  const [activeAttempt, setActiveAttempt] = useState(null);
  const [draftState, setDraftState] = useState(null);
  const [languageLoading, setLanguageLoading] = useState(true);
  const [languageError, setLanguageError] = useState("");
  const [languageNotice, setLanguageNotice] = useState("");
  const [actionBusy, setActionBusy] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draftDirty, setDraftDirty] = useState(false);
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState("Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.");

  const activePanel = normalizePanel(searchParams.get("panel"));

  useEffect(() => {
    document.title = "Experts4Europe | Applicant Mission Control";
  }, []);

  useEffect(() => {
    if (location.hash === "#sprachtest-b1") {
      navigate("/dashboard/applicant/sprachtest-b1", { replace: true });
      return;
    }
    const legacyPanel = searchParams.get("panel");
    if (legacyPanel === "language") {
      navigate("/dashboard/applicant/sprachtest-b1", { replace: true });
      return;
    }
    if (legacyPanel === "matches") {
      navigate("/dashboard/applicant/matches", { replace: true });
      return;
    }
    if (legacyPanel === "messages") {
      navigate("/dashboard/applicant/nachrichten", { replace: true });
    }
  }, [location.hash, navigate, searchParams]);

  async function loadMissionControl() {
    setSnapshotLoading(true);
    setSnapshotError("");
    try {
      setSnapshot(await fetchJson("/api/applicant-dashboard/mission-control"));
    } catch (err) {
      if (handleAuthError(err)) return;
      setSnapshotError(err.message || "Die Mission-Control-Sicht konnte nicht geladen werden.");
    } finally {
      setSnapshotLoading(false);
    }
  }

  async function loadLanguageTestState() {
    setLanguageLoading(true);
    setLanguageError("");
    try {
      const [attemptsData, latestData] = await Promise.all([
        fetchJson("/api/language-tests/b1/me/attempts"),
        fetchJson("/api/language-tests/b1/me/latest"),
      ]);
      const rows = Array.isArray(attemptsData?.rows) ? attemptsData.rows : [];
      const draftAttempt = latestData?.draft_attempt || rows.find((row) => row.status === "draft") || null;
      setAttempts(rows);
      setLatestAttempt(latestData?.attempt || rows.find((row) => row.status !== "draft") || null);
      setActiveAttempt(draftAttempt);
      setDraftState(draftAttempt ? buildDraftState(draftAttempt) : null);
      setDraftDirty(false);
    } catch (err) {
      if (handleAuthError(err)) return;
      setLanguageError(err.message || "Der Sprachtest konnte nicht geladen werden.");
    } finally {
      setLanguageLoading(false);
    }
  }

  async function loadAll() {
    await Promise.all([loadMissionControl(), loadLanguageTestState()]);
  }

  useEffect(() => {
    loadAll();
  }, []);

  function handleAuthError(err) {
    if (Number(err?.status) !== 401) return false;

    clearStoredAuth();
    setDialogOpen(false);
    setActionBusy(false);
    setSnapshotError("");
    setLanguageError("");
    setSnapshotLoading(false);
    setLanguageLoading(false);

    const detail = String(err?.detail || err?.message || "").toLowerCase();
    const isTimeout = detail.includes("expired") || detail.includes("inactivity") || detail.includes("session");

    setSessionExpiredMessage(
      isTimeout
        ? "Du wurdest wegen Inaktivitaet automatisch abgemeldet. Bitte melde dich erneut an."
        : "Deine Anmeldung ist nicht mehr gueltig. Bitte melde dich erneut an."
    );
    setSessionExpiredOpen(true);
    return true;
  }

  function setPanel(panel) {
    const next = new URLSearchParams(searchParams);
    if (panel === "overview") next.delete("panel");
    else next.set("panel", panel);
    setSearchParams(next, { replace: true });
  }

  async function startAttempt(testType) {
    setActionBusy(true);
    setLanguageError("");
    setLanguageNotice("");
    try {
      const result = await fetchJson("/api/language-tests/b1/attempts", { method: "POST", body: JSON.stringify({ test_type: testType }) });
      setActiveAttempt(result.attempt);
      setDraftState(buildDraftState(result.attempt));
      setDraftDirty(false);
      setDialogOpen(true);
      setPanel("language");
      setLanguageNotice(testType === "baseline" ? "Baseline gestartet." : "Folgetest gestartet.");
      await loadAll();
    } catch (err) {
      if (handleAuthError(err)) return;
      setLanguageError(err.message || "Der Test konnte nicht gestartet werden.");
    } finally {
      setActionBusy(false);
    }
  }

  async function saveDraft() {
    if (!activeAttempt || !draftState) return;
    setActionBusy(true);
    setLanguageError("");
    setLanguageNotice("");
    try {
      const result = await fetchJson(`/api/language-tests/b1/attempts/${activeAttempt.id}/draft`, { method: "PATCH", body: JSON.stringify(buildAttemptPayload(draftState)) });
      setActiveAttempt(result.attempt);
      setDraftState(buildDraftState(result.attempt));
      setDraftDirty(false);
      setLanguageNotice("Entwurf gespeichert.");
      await loadAll();
    } catch (err) {
      if (handleAuthError(err)) return;
      setLanguageError(err.message || "Der Entwurf konnte nicht gespeichert werden.");
    } finally {
      setActionBusy(false);
    }
  }

  async function submitAttempt() {
    if (!activeAttempt || !draftState) return;
    setActionBusy(true);
    setLanguageError("");
    setLanguageNotice("");
    try {
      await fetchJson(`/api/language-tests/b1/attempts/${activeAttempt.id}/submit`, { method: "POST", body: JSON.stringify(buildAttemptPayload(draftState)) });
      setDialogOpen(false);
      setActiveAttempt(null);
      setDraftState(null);
      setDraftDirty(false);
      setLanguageNotice("Der B1-Core wurde eingereicht.");
      await loadAll();
    } catch (err) {
      if (handleAuthError(err)) return;
      setLanguageError(err.message || "Der Test konnte nicht eingereicht werden.");
    } finally {
      setActionBusy(false);
    }
  }

  async function uploadSpeakingAudio(file, durationMs) {
    if (!activeAttempt?.id) return;
    setActionBusy(true);
    setLanguageError("");
    setLanguageNotice("");
    try {
      const formData = new FormData();
      formData.append("audio", file);
      formData.append("duration_ms", String(durationMs || 0));
      const result = await fetchJson(`/api/language-tests/b1/attempts/${activeAttempt.id}/speaking-audio`, { method: "POST", body: formData });
      setActiveAttempt(result.attempt);
      setDraftState(buildDraftState(result.attempt));
      setDraftDirty(false);
      setLanguageNotice("Sprachaufnahme gespeichert.");
      await loadAll();
    } catch (err) {
      if (handleAuthError(err)) return;
      setLanguageError(err.message || "Die Sprachaufnahme konnte nicht gespeichert werden.");
      throw err;
    } finally {
      setActionBusy(false);
    }
  }

  async function setEmployerRelease(released) {
    if (!latestAttempt?.id) return;
    setActionBusy(true);
    setLanguageError("");
    setLanguageNotice("");
    try {
      await fetchJson(`/api/language-tests/b1/attempts/${latestAttempt.id}/employer-signal`, { method: "POST", body: JSON.stringify({ released }) });
      setLanguageNotice(released ? "Das Sprachsignal wurde freigegeben." : "Das Sprachsignal wurde fuer Arbeitgeber ausgeblendet.");
      await loadAll();
    } catch (err) {
      if (handleAuthError(err)) return;
      setLanguageError(err.message || "Die Freigabe konnte nicht geaendert werden.");
    } finally {
      setActionBusy(false);
    }
  }

  const mission = snapshot?.mission || {};
  const notifications = snapshot?.notifications || [];
  const opportunities = snapshot?.opportunities || [];
  const primaryAction = snapshot?.actions?.primary;
  const secondaryActions = snapshot?.actions?.secondary || [];
  const motivation = snapshot?.motivation || {};
  const achievementsPreview = snapshot?.achievements_preview || {};
  const hasCompletedAttempt = attempts.some((attempt) => attempt.status === "completed");
  const currentModules = draftState?.modules || {};
  const scoreCards = latestAttempt ? [
    ["Lesen", latestAttempt.reading_score],
    ["Hoeren", latestAttempt.listening_score],
    ["Bausteine", latestAttempt.language_elements_score],
    ["Schreiben", latestAttempt.writing_score],
    ["Sprechen", latestAttempt.speaking_score],
  ] : [];
  const pulseChips = (snapshot?.pulse?.chips || []).filter((chip) => ["employers", "applicants", "jobs_today", "relevance"].includes(chip.key));
  const featuredOpportunity = opportunities[0] || null;
  const secondaryOpportunities = opportunities.slice(1, 4);
  const standingMetrics = [
    { label: "Profil", value: formatOutOfHundred(snapshot?.standing?.score_percentile), note: "in deiner Gruppe" },
    { label: "Deutsch", value: formatOutOfHundred(snapshot?.standing?.language_percentile), note: "in deiner Gruppe" },
    { label: "Sichtbar", value: formatOutOfHundred(snapshot?.standing?.visibility_percentile), note: "fuer Firmen" },
    { label: "Diese Woche", value: motivation.place_label || "Noch offen", note: motivation.change_label || "dein Wochenstand" },
  ];

  const searchItems = useMemo(() => ([
    ...(primaryAction ? [{ href: primaryAction.href, label: primaryAction.label, description: primaryAction.reason }] : []),
    { href: "/dashboard/applicant/erfolge", label: "Erfolge", description: "Deine Medaillen und dein naechster Erfolg" },
    ...secondaryActions.map((item) => ({ href: item.href, label: item.label, description: item.reason })),
    ...opportunities.map((item) => ({ href: item.href, label: item.title, description: `${item.employer_name} · ${item.region_label}` })),
  ]), [opportunities, primaryAction, secondaryActions]);

  const shellNotifications = notifications.map((item) => ({
    key: item.key,
    title: item.title,
    text: item.text,
    href: item.href,
  }));

  const go = (href) => navigate(href);

  return (
    <ApplicantShell
      title="Applicant Mission Control"
      subtitle="Dein Stand, neue Chancen und dein Vergleich in einem Blick."
      actions={
        <>
          <Button variant="outline" className="applicant-cta-button applicant-cta-button-secondary" onClick={() => navigate("/dashboard/applicant/matches")}>Neue Chancen</Button>
          <Button className="applicant-cta-button" onClick={() => primaryAction?.href ? go(primaryAction.href) : setPanel("overview")} disabled={snapshotLoading || actionBusy}>
            {primaryAction?.label || "Mission Control"}
          </Button>
        </>
      }
      notifications={shellNotifications}
      searchItems={searchItems}
    >
      <div className="applicant-dashboard">
        {snapshotError ? <div className="dashboard-inline-alert dashboard-inline-alert-danger">{snapshotError}</div> : null}
        <section className="applicant-pulse-bar">
          {snapshotLoading ? <div className="applicant-empty-card">Live-Signale werden geladen ...</div> : pulseChips.map((chip) => (
            <article key={chip.key} className={`applicant-pulse-chip applicant-pulse-chip--${chip.key}`}>
              <span>{chip.label}</span>
              <strong>{chip.value}</strong>
            </article>
          ))}
        </section>

        <div className="applicant-dashboard__hero-row">
          <section className="applicant-mission-stage">
            <div className="applicant-mission-stage__header">
              <div>
                <span className="applicant-section-kicker">Dein Stand jetzt</span>
                <h2>Wo du stehst. Was du jetzt tun kannst.</h2>
                <p>Dein Score, dein Deutsch und deine Sichtbarkeit zeigen den naechsten Schritt.</p>
              </div>
              <div className="applicant-score-orb">
                <strong>{mission.score_total ?? "--"}</strong>
                <span>Mission Score</span>
                <small>{mission.score_band || "wird geladen"}</small>
              </div>
            </div>

            <div className="applicant-mission-stage__body">
              <div className="applicant-mission-stage__core">
                <div className="applicant-status-row">
                  <span className="applicant-status-pill"><Gauge size={15} />{mission.overall_status_label || "Status"}</span>
                  <span className="applicant-status-pill"><Languages size={15} />{mission.language_status_label || "Deutsch offen"}</span>
                  <span className="applicant-status-pill"><ShieldCheck size={15} />{mission.visibility_status_label || "Noch intern"}</span>
                </div>

                <div className="applicant-mission-stage__next">
                  <span className="applicant-section-kicker">Dein naechster Schritt</span>
                  <strong>{mission.next_best_action_label || "Mission Control wird geladen"}</strong>
                  <p>{mission.next_best_action_reason || "Dein naechster Schritt kommt aus deinem aktuellen Stand."}</p>
                  {primaryAction?.href ? <button type="button" className="applicant-inline-link" onClick={() => go(primaryAction.href)}><span>Jetzt ausfuehren</span><ChevronRight size={15} /></button> : null}
                </div>
              </div>

              <div className="applicant-mission-stage__signals">
                {[
                  ["Wie stark ist dein Profil?", `${snapshot?.score?.profile_completion ?? "--"}%`, "dein Profil"],
                  ["Dein Vergleich", motivation.compare_label || "Noch offen", motivation.compare_detail || "deine Gruppe"],
                  ["Bestes Deutsch", motivation.personal_best_label || "Noch offen", motivation.personal_best_detail || "dein bester Wert"],
                  ["Diese Woche", motivation.week_label || "Noch offen", motivation.week_reason || "dein Wochenstand"],
                ].map(([label, value, note]) => (
                  <article key={label} className="applicant-signal-tile">
                    <span>{label}</span>
                    <strong>{value}</strong>
                    <small>{note}</small>
                  </article>
                ))}
              </div>
            </div>

            <div className="applicant-mission-stage__footer">
              <div className="applicant-mission-stage__footer-item">
                <span className="applicant-section-kicker">Deine Gruppe</span>
                <strong>{snapshot?.standing?.similar_group || "Deine Gruppe"}</strong>
                <small>{snapshot?.standing?.cohort_size || 0} aehnliche Bewerber</small>
              </div>
              <div className="applicant-mission-stage__footer-item">
                <span className="applicant-section-kicker">Sichtbar</span>
                <strong>{mission.visibility_status_label || "Noch intern"}</strong>
                <small>{latestAttempt?.employer_signal_released ? "Firmen koennen dein Signal sehen." : "Dein Signal bleibt noch intern."}</small>
              </div>
            </div>
          </section>

          <aside className="applicant-action-rail">
            <section className="applicant-action-rail__card applicant-action-rail__card-primary">
              <span className="applicant-section-kicker">Mach das jetzt</span>
              <strong>{primaryAction?.label || "Mission Control aktiv"}</strong>
              <p>{primaryAction?.reason || "Das bringt dir jetzt den groessten Schritt nach vorn."}</p>
              <div className="applicant-action-rail__meta">
                <span>{mission.visibility_status_label || "Noch intern"}</span>
                <span>{mission.language_status_label || "Deutsch offen"}</span>
              </div>
              <Button className="applicant-cta-button" onClick={() => primaryAction?.href ? go(primaryAction.href) : setPanel("overview")}>Jetzt oeffnen</Button>
            </section>
            <section className="applicant-action-rail__card">
              <span className="applicant-section-kicker">Dann das</span>
              <div className="applicant-action-list">
                {secondaryActions.length ? secondaryActions.map((item) => (
                  <button key={item.key} type="button" className="applicant-action-list__item" onClick={() => go(item.href)}>
                    <div><strong>{item.label}</strong><span>{item.reason}</span></div>
                    <ArrowRight size={15} />
                  </button>
                )) : <div className="applicant-empty-card">Keine zweite Prioritaet offen.</div>}
              </div>
              <button type="button" className="applicant-achievement-teaser" onClick={() => go("/dashboard/applicant/erfolge")}>
                <div className="applicant-achievement-teaser__icon"><Medal size={18} strokeWidth={2} /></div>
                <div>
                  <strong>{achievementsPreview?.next_medal?.label || "Deine Erfolge"}</strong>
                  <span>{achievementsPreview?.next_medal ? `${achievementsPreview.next_medal.progress_percent}% bis zur naechsten Medaille` : `${achievementsPreview?.unlocked_count || 0} Erfolge freigeschaltet`}</span>
                </div>
                <ChevronRight size={16} />
              </button>
            </section>
          </aside>
        </div>

        <div className="applicant-support-grid">
          <section className="applicant-module-card">
            <div className="applicant-module-card__head"><div><span className="applicant-section-kicker">Neue Chancen</span><h3>Neue Chancen</h3></div></div>
            {featuredOpportunity ? (
              <div className="applicant-opportunity-radar">
                <article className="applicant-opportunity-feature">
                  <div className="applicant-opportunity-card__meta">
                    <span className="applicant-opportunity-card__fit">{featuredOpportunity.fit_band}</span>
                    <small>{formatRelative(featuredOpportunity.released_at)}</small>
                  </div>
                  <strong>{featuredOpportunity.title}</strong>
                  <p>{featuredOpportunity.employer_name} · {featuredOpportunity.region_label}</p>
                  <div className="applicant-opportunity-feature__stats">
                    <div><span>Fit</span><strong>{featuredOpportunity.fit_score}</strong></div>
                    <div><span>Arbeitgeber</span><strong>{featuredOpportunity.employer_name}</strong></div>
                    <div><span>Region</span><strong>{featuredOpportunity.region_label}</strong></div>
                  </div>
                  <button type="button" className="applicant-inline-link" onClick={() => go(featuredOpportunity.href)}><span>Chance oeffnen</span><ChevronRight size={15} /></button>
                </article>
                <div className="applicant-opportunity-stack">
                  {secondaryOpportunities.map((item) => (
                    <article key={item.job_id} className="applicant-opportunity-card">
                      <div className="applicant-opportunity-card__meta"><span className="applicant-opportunity-card__fit">{item.fit_band}</span><small>{formatRelative(item.released_at)}</small></div>
                      <strong>{item.title}</strong>
                      <p>{item.employer_name} · {item.region_label}</p>
                      <div className="applicant-opportunity-card__foot"><span>Fit {item.fit_score}</span><button type="button" className="applicant-inline-link" onClick={() => go(item.href)}><span>Zur Chance</span><ChevronRight size={15} /></button></div>
                    </article>
                  ))}
                </div>
              </div>
            ) : <div className="applicant-empty-card">Noch keine neue relevante Chance ausserhalb deiner laufenden Bewerbungen.</div>}
          </section>

          <section className="applicant-module-card">
            <div className="applicant-module-card__head"><div><span className="applicant-section-kicker">Dein Vergleich</span><h3>Dein Platz in deiner Gruppe</h3></div></div>
            <div className="applicant-standing-board">
              <article className="applicant-standing-board__hero">
                <span className="applicant-section-kicker">Deine Gruppe</span>
                <strong>{motivation.compare_label || "Noch offen"}</strong>
                <p>{snapshot?.standing?.cohort_size || 0} aehnliche Bewerber · {motivation.compare_detail || "dein Vergleich"}</p>
                <div className="applicant-standing-board__momentum">{motivation.language_compare_label || "Deutsch noch offen"}</div>
              </article>
              <div className="applicant-standing-board__metrics">
                {standingMetrics.map((item) => (
                  <article key={item.label} className="applicant-standing-row">
                    <div className="applicant-standing-row__head">
                      <span>{item.label}</span>
                      <strong>{item.value}</strong>
                    </div>
                    <div className="applicant-standing-row__track">
                      <span style={{ width: `${item.label === "Diese Woche" ? Math.min(100, Math.max(18, (snapshot?.standing?.weekly_points || 0) * 12)) : toPercent(
                        item.label === "Profil"
                          ? snapshot?.standing?.score_percentile
                          : item.label === "Deutsch"
                            ? snapshot?.standing?.language_percentile
                            : snapshot?.standing?.visibility_percentile
                      )}%` }} />
                    </div>
                    <small>{item.note}</small>
                  </article>
                ))}
              </div>
            </div>
          </section>
        </div>

        <section className="applicant-local-layer">
          <div className="applicant-local-layer__head">
            <div>
              <span className="applicant-section-kicker">Lokale Ebene</span>
              <h3>Mehr Details</h3>
            </div>
          </div>

          <section className="applicant-secondary-board applicant-local-layer__panel">
            <Tabs defaultValue="target">
              <TabsList className="applicant-tablist applicant-tablist-secondary">
                <TabsTrigger value="target" className="applicant-tabtrigger">Was passiert gerade?</TabsTrigger>
                <TabsTrigger value="drivers" className="applicant-tabtrigger">So wirst du besser</TabsTrigger>
              </TabsList>
              <TabsContent value="target" className="applicant-tabcontent">
                <div className="applicant-market-grid">
                  <article className="applicant-market-card"><strong>{snapshot?.pulse?.employersOnline ?? "--"}</strong><span>Arbeitgeber online jetzt</span></article>
                  <article className="applicant-market-card"><strong>{snapshot?.pulse?.releasedJobsToday ?? "--"}</strong><span>Heute neu freigeschaltete Anzeigen</span></article>
                  <article className="applicant-market-list"><span>Berufsgruppen</span><strong>{snapshot?.pulse?.activeProfessions?.map((item) => item.label).join(" · ") || "Noch offen"}</strong></article>
                  <article className="applicant-market-list"><span>Regionen</span><strong>{snapshot?.pulse?.activeRegions?.map((item) => item.label).join(" · ") || "Noch offen"}</strong></article>
                </div>
              </TabsContent>
              <TabsContent value="drivers" className="applicant-tabcontent">
                <div className="applicant-driver-grid">
                  <div className="applicant-driver-zone applicant-driver-zone-positive">
                    <span className="applicant-driver-grid__label">Hilft dir</span>
                    <div className="applicant-driver-stack">
                      {snapshot?.drivers?.length ? snapshot.drivers.map((item) => <button key={item.label} type="button" className="applicant-driver-card is-positive" onClick={() => go(item.href)}><strong>{item.label}</strong><span>{item.action}</span></button>) : <div className="applicant-empty-card">Noch kein Treiber sichtbar.</div>}
                    </div>
                  </div>
                  <div className="applicant-driver-zone applicant-driver-zone-negative">
                    <span className="applicant-driver-grid__label">Bremst dich</span>
                    <div className="applicant-driver-stack">
                      {snapshot?.blockers?.length ? snapshot.blockers.map((item) => <button key={item.label} type="button" className="applicant-driver-card is-negative" onClick={() => go(item.href)}><strong>{item.label}</strong><span>{item.action}</span></button>) : <div className="applicant-empty-card">Keine akute Bremse sichtbar.</div>}
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </section>

          <section className="applicant-detail-tabs applicant-local-layer__panel">
            <Tabs value={activePanel} onValueChange={setPanel}>
              <TabsList className="applicant-tablist">
                <TabsTrigger value="overview" className="applicant-tabtrigger">Uebersicht</TabsTrigger>
                <TabsTrigger value="history" className="applicant-tabtrigger">Verlauf</TabsTrigger>
                <TabsTrigger value="visibility" className="applicant-tabtrigger">Freigabe</TabsTrigger>
              </TabsList>
              <TabsContent value="overview" className="applicant-tabcontent">
                <div className="applicant-overview-grid">
                  <article className="applicant-detail-card"><span className="applicant-section-kicker">Dein Stand</span><strong>{snapshot?.profile?.display_name || "Applicant"}</strong><p>Mitglied seit {formatDate(snapshot?.profile?.member_since)} · Zielbereich {snapshot?.meta?.target_profession || "wird noch erkannt"}.</p></article>
                  <article className="applicant-detail-card"><span className="applicant-section-kicker">Dein Profil</span><strong>{snapshot?.meta?.skills_count ?? 0} Skills · {snapshot?.meta?.applications_count ?? 0} aktive Bewerbungen</strong><p>CV, Skills und Deutsch bleiben erreichbar, aber nicht immer offen sichtbar.</p></article>
                  <article className="applicant-detail-card"><span className="applicant-section-kicker">Appearance</span><strong>Tag ist Standard</strong><p>Nachtmodus nutzt dieselben Komponenten und dieselbe Applicant-Struktur.</p></article>
                </div>
              </TabsContent>
              <TabsContent value="history" className="applicant-tabcontent">
                <div className="applicant-history-stack">
                  {attempts.length ? attempts.map((attempt) => <article key={attempt.id} className="applicant-history-card"><div><strong>Sprachtest #{attempt.id}</strong><p>{formatAttemptType(attempt.test_type)} · {formatReviewStatus(attempt.review_status)}</p></div><div className="applicant-history-card__meta"><span>{attempt.estimated_cefr_band || "Noch offen"}</span><small>{formatDate(attempt.completed_at || attempt.created_at)}</small></div></article>) : <div className="applicant-empty-card">Noch keine Versuchshistorie vorhanden.</div>}
                </div>
              </TabsContent>
              <TabsContent value="visibility" className="applicant-tabcontent">
                <div className="applicant-overview-grid">
                  <article className="applicant-detail-card"><span className="applicant-section-kicker">Freigabestand</span><strong>{latestAttempt?.employer_signal_released ? "Signal geteilt" : "Signal intern"}</strong><p>{latestAttempt ? `Review: ${formatReviewStatus(latestAttempt.review_status)}` : "Freigabe wird nach dem ersten B1-Core relevant."}</p>{latestAttempt ? <div className="applicant-detail-card__actions"><Button variant="outline" className="applicant-cta-button applicant-cta-button-secondary" onClick={() => setEmployerRelease(false)} disabled={actionBusy}>Intern halten</Button><Button className="applicant-cta-button" onClick={() => setEmployerRelease(true)} disabled={actionBusy}>Freigeben</Button></div> : null}</article>
                  <article className="applicant-detail-card"><span className="applicant-section-kicker">Was geteilt wird</span><strong>Nur kompakte Signale</strong><p>CEFR-Band, Gesamtwert und Trend. Audio, Schreibtext und Detailrubrics bleiben intern.</p></article>
                </div>
              </TabsContent>
            </Tabs>
          </section>
        </section>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent fullScreen className="dashboard-dialog">
          <DialogHeader className="dashboard-dialog__header">
            <DialogTitle className="dashboard-dialog__title">Sprachtest B1 Core</DialogTitle>
            <DialogDescription className="dashboard-dialog__description">Deterministische Kernmodule, KI-gestuetztes Schreiben und Audioaufnahme fuer Sprechen.</DialogDescription>
          </DialogHeader>
          <div className="dashboard-dialog__content">
            <div className="dashboard-dialog__main">
              <section className="dashboard-dialog__section"><h3>Lesen</h3>{renderChoiceModule(currentModules.reading?.items, "reading", draftState?.reading, (moduleKey, itemId, value) => { setDraftState((current) => ({ ...current, [moduleKey]: { ...(current?.[moduleKey] || {}), [itemId]: Number(value) } })); setDraftDirty(true); })}</section>
              <section className="dashboard-dialog__section"><h3>Hoeren</h3>{renderChoiceModule(currentModules.listening?.items, "listening", draftState?.listening, (moduleKey, itemId, value) => { setDraftState((current) => ({ ...current, [moduleKey]: { ...(current?.[moduleKey] || {}), [itemId]: Number(value) } })); setDraftDirty(true); })}</section>
              <section className="dashboard-dialog__section"><h3>Sprachbausteine</h3>{renderChoiceModule(currentModules.language_elements?.items, "language_elements", draftState?.language_elements, (moduleKey, itemId, value) => { setDraftState((current) => ({ ...current, [moduleKey]: { ...(current?.[moduleKey] || {}), [itemId]: Number(value) } })); setDraftDirty(true); })}</section>
              <section className="dashboard-dialog__section"><h3>Schreiben</h3><div className="dashboard-question-card"><p className="dashboard-question-card__copy">{currentModules.writing?.instructions}</p><textarea className="dashboard-textarea" placeholder="Schreibe deine Antwort hier." value={draftState?.writing_answer || ""} onChange={(event) => { setDraftState((current) => ({ ...current, writing_answer: event.target.value })); setDraftDirty(true); }} /></div></section>
              <section className="dashboard-dialog__section"><h3>Sprechen</h3><div className="dashboard-question-card"><p className="dashboard-question-card__meta">Audioaufgabe</p><ul className="dashboard-bullet-list">{(currentModules.speaking?.prompts || []).map((prompt) => <li key={prompt}>{prompt}</li>)}</ul></div><B1SpeakingAudioRecorder attemptId={activeAttempt?.id} currentAudio={activeAttempt?.speaking_audio || draftState?.speaking_audio_meta} speakingStatus={activeAttempt?.speaking_status || draftState?.speaking_status} invalidReason={activeAttempt?.speaking_invalid_reason || draftState?.speaking_invalid_reason} prompts={currentModules.speaking?.prompts || []} disabled={actionBusy || !activeAttempt} onUpload={uploadSpeakingAudio} /></section>
            </div>
            <aside className="dashboard-dialog__side">
              <section className="dashboard-dialog__section"><h3>Versuchssteuerung</h3><div className="dashboard-surface-grid"><div className="dashboard-surface"><span className="dashboard-summary-label">Versuchstyp</span><strong>{formatAttemptType(activeAttempt?.test_type)}</strong></div><div className="dashboard-surface"><span className="dashboard-summary-label">Core-Version</span><strong>{activeAttempt?.core_version || "b1_core_v1"}</strong></div><div className="dashboard-surface"><span className="dashboard-summary-label">Entwurfsstand</span><strong>{draftDirty ? "Ungespeichert" : "Synchron"}</strong></div></div><div className="dashboard-inline-alert">Lesen, Hoeren und Sprachbausteine werden deterministisch bewertet. Schreiben und Sprechen bleiben KI-gestuetzt.</div><div className="dashboard-dialog__actions"><Button variant="outline" className="dashboard-action-button dashboard-action-button-secondary" onClick={saveDraft} disabled={actionBusy || !activeAttempt}>Entwurf speichern</Button><Button className="dashboard-action-button" onClick={submitAttempt} disabled={actionBusy || !activeAttempt}>B1-Core absenden</Button></div></section>
              <section className="dashboard-dialog__section"><h3>Historie</h3><Accordion type="single" collapsible className="dashboard-accordion"><AccordionItem value="attempts" className="dashboard-accordion__item"><AccordionTrigger className="dashboard-accordion__trigger">Letzte Versuche</AccordionTrigger><AccordionContent className="dashboard-accordion__content"><div className="dashboard-history-list">{attempts.length ? attempts.slice(0, 6).map((attempt) => <div key={attempt.id} className="dashboard-history-item"><div><strong>Versuch #{attempt.id}</strong><p>{formatAttemptType(attempt.test_type)} · {formatReviewStatus(attempt.review_status)}</p></div><div className="dashboard-history-meta"><span>{attempt.estimated_cefr_band || "Noch offen"}</span><small>{formatDate(attempt.completed_at || attempt.created_at)}</small></div></div>) : <div className="dashboard-empty-tile">Noch keine gespeicherten Versuche vorhanden.</div>}</div></AccordionContent></AccordionItem></Accordion></section>
            </aside>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={sessionExpiredOpen} onOpenChange={() => {}}>
        <DialogContent className="max-w-[460px]">
          <DialogHeader>
            <DialogTitle>Sitzung abgelaufen</DialogTitle>
            <DialogDescription>{sessionExpiredMessage}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap justify-end gap-3">
            <Button
              variant="outline"
              className="applicant-cta-button applicant-cta-button-secondary"
              onClick={() => navigate("/")}
            >
              Startseite
            </Button>
            <Button
              className="applicant-cta-button"
              onClick={() => navigate("/login", { replace: true })}
            >
              Erneut anmelden
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </ApplicantShell>
  );
}
